import React, { useState, useEffect } from 'react';
import { Save, Eye, Upload, RefreshCw, Image, AlertTriangle, Building2, LogIn, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IsolirGlobalEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingPayment, setUploadingPayment] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingLoginLogo, setUploadingLoginLogo] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [activeTab, setActiveTab] = useState('isolir');
  
  // Isolir Config
  const [config, setConfig] = useState({
    title: 'Layanan Internet Ditangguhkan',
    heading: 'Layanan Internet Ditangguhkan',
    subtitle: 'Akses internet Anda telah ditangguhkan sementara karena terdapat tagihan yang belum diselesaikan.',
    status_title: '⚠ Status: Menunggu Pembayaran',
    status_desc: 'Silakan segera lakukan pembayaran untuk mengaktifkan kembali layanan internet Anda.',
    payment_title: '💳 Metode Pembayaran Tersedia',
    info_text: 'Untuk informasi lebih lanjut, silakan hubungi <strong>provider internet</strong> Anda.',
    footer_text: 'Layanan internet akan aktif kembali secara otomatis setelah pembayaran dikonfirmasi.',
    payment_image_base64: '',
    company_logo_base64: ''
  });

  // Login Branding Config
  const [branding, setBranding] = useState({
    company_name: 'ISP Billing',
    tagline: 'Billing Management System',
    logo_base64: ''
  });

  useEffect(() => {
    fetchConfig();
    fetchBranding();
    setPreviewUrl(`${API_URL}/api/isolir/public/default?t=${Date.now()}`);
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v2/isolir-global/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.data) {
        setConfig(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v2/public/branding`);
      if (response.data.success && response.data.data) {
        setBranding(prev => ({ ...prev, ...response.data.data }));
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`${API_URL}/api/v2/isolir-global/config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Halaman isolir berhasil diperbarui!');
        setPreviewUrl(`${API_URL}/api/isolir/public/default?t=${Date.now()}`);
      } else {
        toast.error(response.data.message || 'Gagal menyimpan');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSavingBranding(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.put(`${API_URL}/api/v2/public/branding`, branding, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('Branding halaman login berhasil diperbarui!');
      } else {
        toast.error(response.data.message || 'Gagal menyimpan');
      }
    } catch (error) {
      console.error('Error saving branding:', error);
      toast.error('Gagal menyimpan branding');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleImageUpload = async (event, imageType) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Gunakan file PNG atau JPG');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File terlalu besar. Maksimal 5MB');
      return;
    }

    const setUploading = imageType === 'payment' ? setUploadingPayment : 
                         imageType === 'logo' ? setUploadingLogo : setUploadingLoginLogo;

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', imageType);
      
      const response = await axios.post(
        `${API_URL}/api/v2/isolir-global/upload-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        if (imageType === 'login_logo') {
          setBranding(prev => ({ ...prev, logo_base64: response.data.base64 }));
          toast.success('Logo login berhasil diupload!');
        } else if (imageType === 'payment') {
          setConfig(prev => ({ ...prev, payment_image_base64: response.data.base64 }));
          toast.success('Logo pembayaran berhasil diupload!');
        } else {
          setConfig(prev => ({ ...prev, company_logo_base64: response.data.base64 }));
          toast.success('Logo perusahaan berhasil diupload!');
        }
      } else {
        toast.error(response.data.message || 'Gagal upload');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Gagal upload gambar');
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleBrandingChange = (field, value) => {
    setBranding(prev => ({ ...prev, [field]: value }));
  };

  const refreshPreview = () => {
    setPreviewUrl(`${API_URL}/api/isolir/public/default?t=${Date.now()}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="w-6 h-6 text-blue-500" />
            Pengaturan Branding & Isolir
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Kelola tampilan halaman login dan halaman isolir
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="login" className="flex items-center gap-2">
            <LogIn className="w-4 h-4" />
            Halaman Login
          </TabsTrigger>
          <TabsTrigger value="isolir" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Halaman Isolir
          </TabsTrigger>
        </TabsList>

        {/* LOGIN BRANDING TAB */}
        <TabsContent value="login" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {/* Logo Login */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Logo Halaman Login
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Upload Logo</Label>
                    <p className="text-xs text-gray-500 mb-2 dark:text-slate-400">Logo akan ditampilkan di halaman login</p>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => handleImageUpload(e, 'login_logo')}
                        className="hidden"
                        id="login-logo-upload"
                      />
                      <label htmlFor="login-logo-upload">
                        <Button asChild variant="outline" disabled={uploadingLoginLogo}>
                          <span>
                            {uploadingLoginLogo ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {uploadingLoginLogo ? 'Uploading...' : 'Pilih Logo'}
                          </span>
                        </Button>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Format: PNG, JPG. Maks 5MB</p>
                  </div>

                  {branding.logo_base64 && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <Label className="mb-2 block">Preview Logo:</Label>
                      <img
                        src={branding.logo_base64 || ""}
                        alt="Login Logo"
                        className="max-w-[200px] h-auto rounded border"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Teks Branding */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Teks Branding</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Nama Perusahaan</Label>
                    <Input
                      value={branding.company_name}
                      onChange={(e) => handleBrandingChange('company_name', e.target.value)}
                      placeholder="ISP Billing"
                    />
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Ditampilkan di bawah logo</p>
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={branding.tagline}
                      onChange={(e) => handleBrandingChange('tagline', e.target.value)}
                      placeholder="Billing Management System"
                    />
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Ditampilkan di bawah nama perusahaan</p>
                  </div>
                </CardContent>
              </Card>

              <Button onClick={handleSaveBranding} disabled={savingBranding} className="w-full">
                {savingBranding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Simpan Branding Login
              </Button>
            </div>

            {/* Preview Login */}
            <div className="space-y-4">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Preview Halaman Login
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8">
                    <div className="bg-white rounded-2xl p-6 max-w-sm mx-auto text-center shadow-xl dark:bg-slate-800">
                      {branding.logo_base64 ? (
                        <img 
                          src={branding.logo_base64 || ""}
                          alt="Logo"
                          className="h-16 mx-auto mb-4"
                        />
                      ) : (
                        <div className="h-16 bg-gray-200 rounded mb-4 flex items-center justify-center text-gray-400 dark:bg-slate-700">
                          Logo
                        </div>
                      )}
                      <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">{branding.company_name || 'ISP Billing'}</h2>
                      <p className="text-gray-500 text-sm mb-4 dark:text-slate-400">{branding.tagline || 'Billing Management System'}</p>
                      <div className="space-y-3">
                        <div className="bg-gray-100 rounded-lg p-3 text-left text-gray-400 text-sm dark:bg-slate-800">Username</div>
                        <div className="bg-gray-100 rounded-lg p-3 text-left text-gray-400 text-sm dark:bg-slate-800">Password</div>
                        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg p-3 text-center font-medium">Login</div>
                      </div>
                      <p className="text-gray-400 text-xs mt-4">© 2025 {branding.company_name || 'ISP Billing'}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center dark:text-slate-400">
                    * Preview menunjukkan tampilan setelah save
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ISOLIR TAB */}
        <TabsContent value="isolir" className="mt-6">
          <div className="flex justify-end gap-2 mb-4">
            <Button variant="outline" onClick={refreshPreview}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Preview
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Simpan Isolir
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor Panel */}
            <div className="space-y-4">
              {/* Logo Perusahaan */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-500" />
                    Logo Perusahaan (Isolir)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Upload Logo Perusahaan</Label>
                    <p className="text-xs text-gray-500 mb-2 dark:text-slate-400">Logo akan ditampilkan di bagian atas halaman isolir</p>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => handleImageUpload(e, 'logo')}
                        className="hidden"
                        id="company-logo-upload"
                      />
                      <label htmlFor="company-logo-upload">
                        <Button asChild variant="outline" disabled={uploadingLogo}>
                          <span>
                            {uploadingLogo ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {uploadingLogo ? 'Uploading...' : 'Pilih Logo'}
                          </span>
                        </Button>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Format: PNG, JPG. Maks 5MB. Rekomendasi: 200x200px</p>
                  </div>

                  {config.company_logo_base64 && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <Label className="mb-2 block">Preview Logo:</Label>
                      <img
                        src={config.company_logo_base64 || ""}
                        alt="Company Logo"
                        className="max-w-[150px] h-auto rounded border"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Teks Utama */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Teks Utama</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Judul Halaman (Title)</Label>
                    <Input
                      value={config.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="Layanan Internet Ditangguhkan"
                    />
                  </div>
                  <div>
                    <Label>Heading</Label>
                    <Input
                      value={config.heading}
                      onChange={(e) => handleInputChange('heading', e.target.value)}
                      placeholder="Layanan Internet Ditangguhkan"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea
                      value={config.subtitle}
                      onChange={(e) => handleInputChange('subtitle', e.target.value)}
                      placeholder="Deskripsi alasan isolir..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Status Box */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Kotak Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Judul Status</Label>
                    <Input
                      value={config.status_title}
                      onChange={(e) => handleInputChange('status_title', e.target.value)}
                      placeholder="⚠ Status: Menunggu Pembayaran"
                    />
                  </div>
                  <div>
                    <Label>Deskripsi Status</Label>
                    <Textarea
                      value={config.status_desc}
                      onChange={(e) => handleInputChange('status_desc', e.target.value)}
                      placeholder="Silakan segera lakukan pembayaran..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Logo Pembayaran */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Logo Metode Pembayaran
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Judul Section Pembayaran</Label>
                    <Input
                      value={config.payment_title}
                      onChange={(e) => handleInputChange('payment_title', e.target.value)}
                      placeholder="💳 Metode Pembayaran Tersedia"
                    />
                  </div>
                  
                  <div>
                    <Label>Upload Logo Pembayaran Baru</Label>
                    <div className="mt-2">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => handleImageUpload(e, 'payment')}
                        className="hidden"
                        id="payment-image-upload"
                      />
                      <label htmlFor="payment-image-upload">
                        <Button asChild variant="outline" disabled={uploadingPayment}>
                          <span>
                            {uploadingPayment ? (
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-2" />
                            )}
                            {uploadingPayment ? 'Uploading...' : 'Pilih Gambar'}
                          </span>
                        </Button>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 dark:text-slate-400">Format: PNG, JPG. Maks 5MB</p>
                  </div>

                  {config.payment_image_base64 && (
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <Label className="mb-2 block">Preview Logo:</Label>
                      <img
                        src={config.payment_image_base64 || ""}
                        alt="Payment Methods"
                        className="max-w-full h-auto rounded border"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Footer */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Footer & Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Info Text (support HTML)</Label>
                    <Textarea
                      value={config.info_text}
                      onChange={(e) => handleInputChange('info_text', e.target.value)}
                      placeholder="Untuk informasi lebih lanjut..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Footer Text</Label>
                    <Textarea
                      value={config.footer_text}
                      onChange={(e) => handleInputChange('footer_text', e.target.value)}
                      placeholder="Layanan akan aktif kembali..."
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Preview Panel */}
            <div className="space-y-4">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Preview Halaman Isolir
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg overflow-hidden bg-gray-100 dark:bg-slate-800" style={{ height: '600px' }}>
                    <iframe
                      src={previewUrl}
                      className="w-full h-full"
                      title="Isolir Preview"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center dark:text-slate-400">
                    * Preview akan diperbarui setelah menyimpan perubahan
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IsolirGlobalEditor;
